/**
 * Plans and access. Individual = one full-access profile, Family = up to 5 full-access profiles.
 * Free = free content only (intro path, the first lesson of each path, playground preview items).
 */
import {
  INDIVIDUAL_FULL_ACCESS_PROFILES,
  MAX_PROFILES,
  PRICES_USD_CENTS,
  TRIAL_DAYS,
  TRIAL_REMINDER_DAY,
  TRIAL_REMINDER_DAYS_BEFORE_CHARGE,
  type BillingPeriod,
  type PlanId,
} from './constants';

export type Tier = 'free' | PlanId;
export type ProfileAccess = 'full' | 'limited';

export interface EntitlementInfo {
  tier: Tier;
  period: BillingPeriod | null;
  trialEndsAt: string | null;
  expiresAt: string | null;
}

export const FREE_ENTITLEMENT: EntitlementInfo = { tier: 'free', period: null, trialEndsAt: null, expiresAt: null };

export const maxProfiles = (_tier: Tier) => MAX_PROFILES;

/**
 * Access for the profile at `index` (profiles sorted by creation; the premium profile is the
 * first one unless `premiumProfileId` says otherwise).
 */
export function profileAccess(tier: Tier, profileIds: string[], profileId: string, premiumProfileId?: string | null): ProfileAccess {
  if (tier === 'family') return 'full';
  if (tier === 'individual') {
    const premium = premiumProfileId && profileIds.includes(premiumProfileId) ? premiumProfileId : profileIds.slice(0, INDIVIDUAL_FULL_ACCESS_PROFILES)[0];
    return profileId === premium ? 'full' : 'limited';
  }
  return 'limited';
}

/** Adding a full-access profile beyond the individual plan needs the family upgrade paywall. */
export const needsFamilyUpgrade = (tier: Tier, profileCount: number) => tier === 'individual' && profileCount >= INDIVIDUAL_FULL_ACCESS_PROFILES;

export interface PlanOffer {
  plan: PlanId;
  period: BillingPeriod;
  priceCents: number;
  monthlyEquivalentCents: number;
  /** Savings vs paying monthly for a year, in percent (yearly only). */
  discountPercent: number;
  productId: string;
}

export const productId = (plan: PlanId, period: BillingPeriod) => `sketchling.${plan}.${period}`;

export function listOffers(): PlanOffer[] {
  return (Object.keys(PRICES_USD_CENTS) as PlanId[]).flatMap((plan) =>
    (['monthly', 'yearly'] as BillingPeriod[]).map((period) => {
      const priceCents = PRICES_USD_CENTS[plan][period];
      const monthlyEquivalentCents = period === 'yearly' ? Math.round(priceCents / 12) : priceCents;
      const discountPercent = period === 'yearly' ? Math.round((1 - priceCents / (PRICES_USD_CENTS[plan].monthly * 12)) * 100) : 0;
      return { plan, period, priceCents, monthlyEquivalentCents, discountPercent, productId: productId(plan, period) };
    }),
  );
}

/**
 * Trial timeline for the paywall: today → reminder day → charge day. The reminder is sent
 * TRIAL_REMINDER_DAYS_BEFORE_CHARGE days before the charge (day 5 of 7; day 12 of a 14-day invite trial).
 */
export function trialTimeline(start: Date, trialDays: number = TRIAL_DAYS) {
  const day = (n: number) => new Date(start.getTime() + n * 86400_000);
  const reminderDay = trialDays === TRIAL_DAYS ? TRIAL_REMINDER_DAY : Math.max(1, trialDays - TRIAL_REMINDER_DAYS_BEFORE_CHARGE + 1);
  return {
    today: start,
    reminder: day(reminderDay - 1),
    charge: day(trialDays),
    reminderDay,
    chargeDay: trialDays,
  };
}

/** When the "your trial ends soon" reminder is due for a trial that converts at `trialEndsAt`. */
export const trialReminderAt = (trialEndsAt: Date | string) => new Date(new Date(trialEndsAt).getTime() - TRIAL_REMINDER_DAYS_BEFORE_CHARGE * 86400_000);

/* ------------------------------ Subscription lifecycle ------------------------------ */

/**
 * Lifecycle statuses of one subscription record (server `Subscription.status`):
 *  trialing / active   access until the period end
 *  grace               payment failed, store retries: access until `graceEndsAt` (Apple 16 days, Play grace)
 *  billing_issue       payment failed and the grace window is over (Play account hold): no access
 *  paused              Play pause / Stripe pause: access until the current period end, then none
 *  canceled / expired  no access (refunds and revocations are `canceled`)
 *  pending             Ask to Buy / deferred: waiting for a parent, never grants access
 *  queued              gift waiting for a store subscription to end, no access yet
 */
export const SUBSCRIPTION_STATUSES = ['trialing', 'active', 'grace', 'billing_issue', 'paused', 'expired', 'canceled', 'pending', 'queued'] as const;
export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUSES)[number];
/** Where a subscription record comes from. referral / gift / promo are server-side grants that stack. */
export const SUBSCRIPTION_SOURCES = ['stripe', 'app_store', 'play_store', 'promo', 'mock', 'referral', 'gift'] as const;
export type SubscriptionSource = (typeof SUBSCRIPTION_SOURCES)[number];
/** Auto-renewing store / web subscriptions (the user pays and can manage them in the store). */
export const RENEWING_SOURCES: readonly SubscriptionSource[] = ['stripe', 'app_store', 'play_store', 'mock'];
/** Server-side grants (no store involved): they stack after the current coverage. */
export const GRANT_SOURCES: readonly SubscriptionSource[] = ['promo', 'referral', 'gift'];
/** Apple's Billing Grace Period (used when the store does not tell us the grace end). */
export const DEFAULT_GRACE_DAYS = 16;

export interface SubscriptionLike {
  plan: string;
  period: string;
  status: string;
  source: string;
  currentPeriodEnd: Date | string;
  isTrial?: boolean | null;
  graceEndsAt?: Date | string | null;
  willRenew?: boolean | null;
  familyShared?: boolean | null;
}

const ms = (d: Date | string | null | undefined) => (d == null ? 0 : new Date(d).getTime());

/** Instant access ends for one record, or null when it grants no access at `now`. */
export function accessUntil(s: SubscriptionLike, now: Date = new Date()): number | null {
  const t = now.getTime();
  const end = ms(s.currentPeriodEnd);
  switch (s.status) {
    case 'trialing':
    case 'active':
    case 'paused':
      return end > t ? end : null;
    case 'grace': {
      const g = Math.max(end, s.graceEndsAt ? ms(s.graceEndsAt) : end + DEFAULT_GRACE_DAYS * 86400_000);
      return g > t ? g : null;
    }
    default:
      return null;
  }
}

/** Status shown to grown-ups: a grace window that ran out reads as `billing_issue`, a lapsed active one as `expired`. */
export function displayStatus(s: SubscriptionLike, now: Date = new Date()): SubscriptionStatus {
  const st = s.status as SubscriptionStatus;
  if (accessUntil(s, now) != null) return st;
  if (st === 'grace') return 'billing_issue';
  if (st === 'trialing' || st === 'active' || st === 'paused') return 'expired';
  return (SUBSCRIPTION_STATUSES as readonly string[]).includes(st) ? st : 'expired';
}

const PLAN_RANK: Record<string, number> = { individual: 1, family: 2 };

export interface ResolvedEntitlement<S extends SubscriptionLike = SubscriptionLike> {
  tier: Tier;
  /** Record that decides the tier (family > individual, then the latest access end). */
  best: S | null;
  /** Every record granting access now. */
  active: S[];
  /** Latest instant any record grants access (stacked gifts / referral months / store period). */
  accessUntil: string | null;
  familyShared: boolean;
  /** Tester / demo account (server `unlockAll`): everything open regardless of records. */
  unlockAll: boolean;
}

/**
 * Pure entitlement merge: family > individual; grace keeps access; paused / expired / canceled lose it
 * after the period; pending and queued never grant access; gifts, referral months and promos stack
 * (the coverage is the latest end among active records). `unlockAll` (demo account) = Family.
 */
export function resolveEntitlement<S extends SubscriptionLike>(subs: readonly S[], now: Date = new Date(), opts: { unlockAll?: boolean } = {}): ResolvedEntitlement<S> {
  const active = subs
    .filter((s) => s.plan in PLAN_RANK && accessUntil(s, now) != null)
    .sort((a, b) => PLAN_RANK[b.plan]! - PLAN_RANK[a.plan]! || (accessUntil(b, now) ?? 0) - (accessUntil(a, now) ?? 0));
  const best = active[0] ?? null;
  const until = active.reduce((m, s) => Math.max(m, accessUntil(s, now) ?? 0), 0);
  const tier: Tier = opts.unlockAll ? 'family' : best ? (best.plan as PlanId) : 'free';
  return {
    tier,
    best,
    active,
    accessUntil: until ? new Date(until).toISOString() : null,
    familyShared: !!best?.familyShared,
    unlockAll: !!opts.unlockAll,
  };
}

/**
 * Start of a stacked grant (referral month, gift year): after the latest coverage of the user's
 * records, or now. Ask-to-Buy / queued records are ignored.
 */
export function stackStart(subs: readonly SubscriptionLike[], now: Date = new Date()): Date {
  const until = subs.reduce((m, s) => Math.max(m, accessUntil(s, now) ?? 0), now.getTime());
  return new Date(until);
}

export const formatPrice = (cents: number, currency = 'USD', locale = 'en-US') =>
  new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
